import React, { useEffect, useState } from 'react';
import {
  GoogleMap,
  LoadScript,
  Marker,
  OverlayView,
  useJsApiLoader,
} from '@react-google-maps/api';

import { ILocation } from '../../interfaces/locationGlobalMap';
import globalMapApi from '../../api/globalMapApi';
import { useLocation } from 'react-router-dom';

export default function GlobalMapMobileApp() {
  const [date, setDate] = useState('');
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const [routeType, setRouteType] = useState('');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const API_KEY: string = process.env.REACT_APP_API_URL ?? '';
  const [mapCenter, setMapCenter] = useState({
    lat: -16.917601457089138,
    lng: 145.77835451688978,
  });
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const mapContainerStyle = {
    width: '100%',
    height: '500px',
  };

  // Ensure Google Maps API is loaded before rendering the map
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
  });

  useEffect(() => {
    if (isLoaded) {
      fetchCoordinates();
    }
  }, [isLoaded]);

  useEffect(() => {
    const ownerIds = queryParams.get('ownerId');
    const dates = queryParams.get('date');
    const routeTypes = queryParams.get('routeType');

    if (ownerIds) setOwnerId(Number(ownerIds));
    if (dates) setDate(dates.replace(/['"]/g, ''));
    if (routeTypes) setRouteType(routeTypes.replace(/['"]/g, ''));

    if (date && ownerId && routeType) {
      fetchCoordinates();
    }
  }, [date, ownerId, routeType]);

  const fetchCoordinates = async () => {
    console.log('Fetching coordinates with:', { ownerId, date, routeType });
    setLoading(true);
    setError(null);
    setLocations([]);

    try {
      const response = await globalMapApi.getListOfLocations(1, date, routeType);

      if (response.data && response.data.length > 0) {
        setLocations(response.data);
        setMapCenter({
          lat: response.data[0].latitude,
          lng: response.data[0].longitude,
        });
      } else {
        console.warn('No data received, showing default map.');
        setError('No location data available.');
      }
    } catch (error) {
      console.error('Error fetching coordinates:', error);
      setError('Failed to load location data.');
    } finally {
      setLoading(false);
    }
  };

  // Ensure Google API is loaded before accessing google object
  const getMarkerIcon = (vehicleType: string) => {
    if (!isLoaded) return '';
    return vehicleType === 'bus'
      ? { url: 'bus1.png', scaledSize: new window.google.maps.Size(30, 30) }
      : { url: 'balloons.png', scaledSize: new window.google.maps.Size(30, 30) };
  };

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;

  return (
    <div style={{ height: '100%', borderRadius: '15px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '5px',
        }}
      >
        <h1 style={{ fontSize: '24px' }}>Driver And Pilot Live Location</h1>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={10}
      >
        {loading ? (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            Loading...
          </div>
        ) : error ? (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            {error}
          </div>
        ) : locations.length > 0 ? (
          locations.map((location, index) => {
            const lat = location.latitude ?? -16.9176;
            const lng = location.longitude ?? 145.7783;

            if (isNaN(lat) || isNaN(lng)) {
              console.warn(
                `Invalid coordinates for location at index ${index}:`,
                location,
              );
              return null;
            }

            return (
              <Marker
                key={index}
                position={{ lat, lng }}
                onClick={() => setSelectedLocation(location)}
                icon={getMarkerIcon(location.vehicleType)}
              />
            );
          })
        ) : (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            No Data Available
          </div>
        )}

        {isLoaded && selectedLocation && (
          <OverlayView
            position={{
              lat: selectedLocation.latitude,
              lng: selectedLocation.longitude,
            }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '10px',
                borderRadius: '8px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                maxWidth: '250px',
                width: '150px',
                height: '100px',
              }}
            >
              <p>
                <strong>Vehicle Name:</strong> {selectedLocation.vehicleName}
              </p>
              <p style={{ marginTop: '4px' }}>
                <strong>Staff Name:</strong> {selectedLocation.staffName}
              </p>
              <button
                style={{
                  backgroundColor: 'black',
                  color: 'white',
                  borderRadius: '5px',
                  padding: '5px 10px',
                  marginTop: '10px',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedLocation(null)}
              >
                Close
              </button>
            </div>
          </OverlayView>
        )}
      </GoogleMap>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '10px',
        }}
      >
        <button
          style={{
            height: '40px',
            width: '150px',
            backgroundColor: 'black',
            color: 'white',
            borderRadius: '10px',
          }}
          onClick={(e) => {
            e.preventDefault();
            fetchCoordinates();
          }}
        >
          Refresh Here
        </button>
      </div>
    </div>
  );
}
